import { useState, useRef, useEffect, useContext, useMemo, UIEvent, KeyboardEvent } from 'react';
import { Stack, Typography } from '@mui/material';
import { DynamicOption } from '@refineintel/types';
import styles from './RDropdown.module.css';
import { RTextInput } from '@components/RTextInput/RTextInput';
import { ArrowDownIcon } from '@icons/ArrowDownIcon';
import { ArrowUpIcon } from '@icons/ArrowUpIcon';
import { LocalizationContext } from '@context/LocalizationContext';
import { usePostHog } from 'posthog-js/react';

interface Props {
	options: DynamicOption[];
	setSearchValue: (value: string) => void;
	onSelect: (selectedOption: DynamicOption | null) => void;
	value?: DynamicOption | null;
	isLoading?: boolean;
	isValid?: boolean;
	errorMessage?: string;
	required?: boolean;
	placeholder?: string;
	title?: string;
	onScrolledToBottom?: () => void;
	hasMorePages?: boolean;
	/**
	 * Free-text mode (RND-4217, HRR address fields): the typed text is itself the
	 * answer and the options are assistive suggestions only. Three NAICS-bred
	 * behaviors are disabled under this flag — `setSearchValue` receives the RAW
	 * text (no lowercasing, since the parent persists it verbatim), click-outside
	 * closes without auto-selecting the exact-code match, and Enter on an open
	 * dropdown just closes it instead of picking an option. Off (default) keeps
	 * the pick-from-list behavior bit-for-bit for NAICS.
	 */
	freeText?: boolean;
}

/**
 * Prefix-first relevance ordering for the auto-suggest list (RND-4260).
 *
 * The backend `getDynamicOptions` search matches the term ANYWHERE in an
 * option (`key`/`value` contains <q>), so a "contains" hit like `1032545`
 * can be returned ahead of a true prefix hit like `2545001` when searching
 * `2545`. Users expect prefix matches first (same as the NAICS lookup), so we
 * re-order the loaded options on the client: options whose label OR value
 * STARTS WITH the typed term bubble to the top; everything else keeps the
 * backend's original relative order.
 *
 * Implemented as a stable two-bucket sort (Array.prototype.sort is stable in
 * modern JS), so it only promotes prefix matches — it never reshuffles within a
 * bucket. That keeps the result deterministic across appended infinite-scroll
 * pages and leaves the empty-term case (initial open) untouched.
 */
const sortByPrefixRelevance = (options: DynamicOption[], searchTerm: string) => {
	const term = searchTerm.trim().toLowerCase();
	if (!term) return options;

	const isPrefixMatch = (option: DynamicOption) => {
		const label = option.label?.toLowerCase() ?? '';
		const value = option.value?.toLowerCase() ?? '';
		return label.startsWith(term) || value.startsWith(term);
	};

	return [...options].sort((a, b) => Number(isPrefixMatch(b)) - Number(isPrefixMatch(a)));
};

const highlightText = (text: string, searchTerm: string) => {
	if (!searchTerm) return text;

	const lowerText = text.toLowerCase();
	const lowerSearchTerm = searchTerm.toLowerCase();
	const index = lowerText.indexOf(lowerSearchTerm);

	if (index === -1) return text;

	return (
		<>
			{text.slice(0, index)}
			<strong>{text.slice(index, index + searchTerm.length)}</strong>
			{text.slice(index + searchTerm.length)}
		</>
	);
};

export const RDropdown = ({
	options,
	setSearchValue,
	onSelect,
	value,
	isLoading = false,
	isValid = true,
	errorMessage = 'This field is required',
	required = false,
	placeholder,
	title,
	onScrolledToBottom,
	hasMorePages = false,
	freeText = false,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState(value?.label || '');
	const [autocompleteOption, setAutocompleteOption] = useState<DynamicOption | null>(null);
	const { DynamicOptionsDropdown } = useContext(LocalizationContext);
	const posthog = usePostHog();
	const dropdownVariant = posthog.getFeatureFlag('naics-dropdown');
	const dropdownV2 = dropdownVariant === 'opened-focused';

	const wrapperRef = useRef<HTMLDivElement>(null);

	// Prefix matches first, then the backend's original order (RND-4260).
	const sortedOptions = useMemo(() => sortByPrefixRelevance(options, inputValue), [options, inputValue]);

	const handleScroll = (event: UIEvent<HTMLDivElement>) => {
		const target = event.target as HTMLDivElement;
		const scrollThreshold = 50;

		const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= scrollThreshold;

		if (isNearBottom && onScrolledToBottom && hasMorePages && !isLoading) {
			onScrolledToBottom();
		}
	};

	useEffect(() => {
		// Exact-code match is a NAICS affordance (type "541511" → blur/Enter picks
		// the full option). In free-text mode the typed text IS the answer, so no
		// option may ever be picked on the customer's behalf.
		if (freeText) return;
		const autocompletedOption = options.find((option) => {
			const optionCode = option.label.replace(option.value, '').trim();
			return optionCode === inputValue;
		});
		setAutocompleteOption(autocompletedOption || null);
	}, [options, inputValue, freeText]);

	// Reconcile the visible input to a restored/selected `value.label` (RND-4221).
	//
	// `inputValue` is seeded from `value?.label` ONLY in the useState initializer
	// above, then never re-synced. That is fine forward (the user types), but on a
	// goBack remount the parent passes back a restored `value` (a DynamicOption
	// whose `label` is the previously-stored answer) while `inputValue` initialises
	// from the same `value` — so it normally lines up. The failure mode this fixes
	// is when `value.label` arrives/changes AFTER mount (or the initial render
	// happens before `value` is resolved): the hidden typeahead input holds the
	// label but the visible RDropdown shows blank.
	//
	// Guards that make this safe and non-destructive:
	//  - Only ever SETS a non-empty label; it never clears the field. A null/empty
	//    `value` (forward entry, or v1 reset) is a no-op, so typed text is never
	//    wiped by a value going away.
	//  - Functional updater compares against the freshest `inputValue` and bails
	//    when they already match, so it cannot stomp text the user is typing (and
	//    keeps `inputValue` out of the dep array — the effect only re-runs when
	//    `value?.label` itself changes, never per keystroke).
	useEffect(() => {
		const label = value?.label;
		if (!label) return;
		setInputValue((prev) => (prev === label ? prev : label));
	}, [value?.label]);

	const handleInputChange = (value: string) => {
		setInputValue(value);
		// Free-text parents persist this string verbatim as the answer — keep the
		// customer's casing. (BE option search is case-insensitive either way.)
		setSearchValue(freeText ? value : value.toLowerCase());
		if (value.length > 0) {
			setIsOpen(true);
		} else {
			setIsOpen(false);
		}
	};

	const handleInputClick = () => {
		if (dropdownV2) {
			if (inputValue.length > 0) {
				setIsOpen(!isOpen);
			}
			return;
		}
		setIsOpen(!isOpen);
		return;
	};

	const handleOptionClick = (option: DynamicOption) => {
		onSelect(option);
		setInputValue(option.label);
		setIsOpen(false);
	};

	/**
	 * Intercept Enter inside the typeahead input so it does NOT bubble up and
	 * submit the surrounding form (HRR v2 PageRenderer wraps the page in a
	 * `<form onSubmit>` so Enter on a single-line input fires Next — which is
	 * the correct UX for plain text inputs but wrong for an open autocomplete,
	 * where Enter should pick the focused option instead of advancing the page).
	 *
	 * Behavior:
	 *  - If the dropdown is open AND a matching option exists (either an exact
	 *    `autocompleteOption` or, failing that, the first visible option),
	 *    Enter picks it, closes the dropdown, and is swallowed.
	 *  - If the dropdown is closed, Enter is allowed to bubble — the customer
	 *    has already committed a selection (or left the field empty) and the
	 *    parent form's submit handler should advance to the next page.
	 *
	 * v1 (NAICSQuestion) has no surrounding form, so the bubble in the
	 * dropdown-closed branch is a no-op there — this change is backwards-safe.
	 */
	const handleInputKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter') return;
		if (!isOpen) return;
		if (freeText) {
			// The typed text is already committed via setSearchValue — picking
			// options[0] here would silently overwrite the customer's address.
			// First Enter closes the suggestions; the next one (dropdown closed)
			// bubbles to the surrounding form and advances the page.
			event.preventDefault();
			event.stopPropagation();
			setIsOpen(false);
			return;
		}
		const picked = autocompleteOption ?? sortedOptions[0] ?? null;
		if (!picked) {
			// Open dropdown with no matchable option yet — swallow so the form
			// doesn't submit a half-typed search query as an answer.
			event.preventDefault();
			event.stopPropagation();
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		handleOptionClick(picked);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				if (autocompleteOption) {
					handleOptionClick(autocompleteOption);
					setInputValue(autocompleteOption.label);
				}
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [autocompleteOption, inputValue]);

	return (
		<Stack data-cy="pqDropdownContainer" gap={'2px'} width={'100%'}>
			{title && (
				<Typography data-cy="pqDropdownTitle" variant="answerText">
					{title}
				</Typography>
			)}

			<div className={styles.wrapper} ref={wrapperRef} onKeyDown={handleInputKeyDown}>
				<RTextInput
					autoFocus={dropdownV2}
					data-cy="pqDropdownInput"
					endIcon={isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
					onClick={handleInputClick}
					onChange={handleInputChange}
					value={inputValue}
					placeholder={placeholder}
					isValid={isValid}
					errorMessage={errorMessage}
					required={required}
				/>
				{isOpen && (
					<div data-cy="pqDropdownOptionsContainer" className={styles.dropdown} onScroll={handleScroll}>
						{sortedOptions.map((option, index) => (
							<div
								key={option.id}
								data-cy={`pqDropdownOption${index}`}
								className={styles.option}
								onClick={() => handleOptionClick(option)}>
								{highlightText(option.label, inputValue)}
							</div>
						))}

						{isLoading && (
							<div data-cy="pqDropdownLoadingState" className={styles.option}>
								<Typography fontSize="14px">{DynamicOptionsDropdown.LoadingText}</Typography>
							</div>
						)}

						{!isLoading && options.length === 0 && (
							<div data-cy="pqDropdownNoResults" className={styles.option}>
								<Typography fontSize="14px">{DynamicOptionsDropdown.NoResultsText}</Typography>
							</div>
						)}
					</div>
				)}
			</div>
		</Stack>
	);
};
